import { SessionExpiredDialog } from '@rio-cloud/rio-session-expired-info';
import ApplicationLayout from '@rio-cloud/rio-uikit/ApplicationLayout';
import NotificationsContainer from '@rio-cloud/rio-uikit/NotificationsContainer';
import { Outlet } from 'react-router-dom';

import { getLocale } from '../configuration/lang/langSlice';
import { isUserSessionExpired } from '../configuration/login/loginSlice';
import { useAppDispatch, useAppSelector } from '../configuration/setup/hooks';
import { getSessionExpiredAcknowledged, hideSessionExpiredDialog } from '../data/appSlice';
import AppHeader from '../features/header/AppHeader';

import './App.css';
import { useRef, useState } from 'react';
import OnboardingTourContextProvider from '../components/OnboardingTour.context';
import PartnerNetworkOverviewWrapper from '../features/partnerNetworkOverview/PartnerNetworkOverviewWrapper';
import PartnerNetworkOverviewContextProvider from '../features/partnerNetworkOverview/partnerNetworkOverview.context';
import { AppContext } from './AppContext';
import { ServiceInfoDialog } from '../features/serviceInfo/ServiceInfoDialog';
import { useServiceInfoDialogOnStart } from '../features/serviceInfo/useServiceInfoDialogOnStart';
import type { RentalOnboardingStep } from '../features/onboarding/onboardingUtils';

const isInIframe = () => window.self !== window.top;

const AppLayout = () => {
    const [showRentalOnboardingTour, setShowRentalOnboardingTour] = useState(false);
    const [activeStep, setActiveStep] = useState<RentalOnboardingStep | null>(null);

    const dispatch = useAppDispatch();

    const sidebarRef = useRef<HTMLDivElement>(null);

    const isSessionExpired = useAppSelector(isUserSessionExpired);
    const sessionExpiredAcknowledged = useAppSelector(getSessionExpiredAcknowledged);

    const userLocale = useAppSelector(getLocale);

    useServiceInfoDialogOnStart();

    if (!userLocale) {
        return null;
    }

    const handleSessionExpiredDialogClose = () => dispatch(hideSessionExpiredDialog());
    const showSessionExpired = isSessionExpired && !sessionExpiredAcknowledged;

    return (
        <AppContext.Provider value={{ sidebarRef }}>
            <OnboardingTourContextProvider
                showRentalOnboardingTour={showRentalOnboardingTour}
                setShowRentalOnboardingTour={setShowRentalOnboardingTour}
                activeStep={activeStep}
                setActiveStep={setActiveStep}
            >
                <PartnerNetworkOverviewContextProvider>
                    <ApplicationLayout data-testid={'app-layout'}>
                        {isInIframe() ? undefined : (
                            <ApplicationLayout.Header>
                                <AppHeader />
                            </ApplicationLayout.Header>
                        )}
                        <ApplicationLayout.Sidebar className='right' ref={sidebarRef} />

                        <ApplicationLayout.Body>
                            <NotificationsContainer />

                            <SessionExpiredDialog
                                locale={userLocale}
                                onClose={handleSessionExpiredDialogClose}
                                show={showSessionExpired}
                            />

                            <ServiceInfoDialog />

                            <PartnerNetworkOverviewWrapper />

                            <Outlet />
                        </ApplicationLayout.Body>
                    </ApplicationLayout>
                </PartnerNetworkOverviewContextProvider>
            </OnboardingTourContextProvider>
        </AppContext.Provider>
    );
};

export default AppLayout;
